import { cx } from '@flexbyt/widget-preview';
import type { ReactNode } from 'react';
import { LightboxProvider, useLightbox, ZoomIcon, type Frame } from './Lightbox.js';

/** Lexical editor state → React. Unknown node types still render their children. */

/** Lexical's text format bitmask. */
const IS_BOLD = 1;
const IS_ITALIC = 1 << 1;
const IS_STRIKETHROUGH = 1 << 2;
const IS_UNDERLINE = 1 << 3;
const IS_CODE = 1 << 4;
const IS_SUBSCRIPT = 1 << 5;
const IS_SUPERSCRIPT = 1 << 6;

type Node = {
  type?: string;
  children?: Node[];
  text?: string;
  format?: number | string;
  tag?: string;
  listType?: string;
  url?: string;
  newTab?: boolean;
  fields?: { url?: string; newTab?: boolean; doc?: unknown };
  value?: unknown;
  [key: string]: unknown;
};

export function RichText({ content }: { content: unknown }) {
  const root = (content as { root?: Node } | null)?.root;
  if (!root?.children?.length) return null;

  return (
    <LightboxProvider>
      {/*
        One measure for the whole article, set here rather than on each block, so
        a heading and the paragraph under it share a left edge — the same rule the
        rest of the site follows with `Measure`. Figures sit inside it too: an
        image wider than the column it illustrates reads as a different page
        element, and the full capture is one click away.
      */}
      <div className="max-w-prose text-body-lg text-ink-700">
        <Children nodes={root.children} />
      </div>
    </LightboxProvider>
  );
}

function Children({ nodes }: { nodes?: Node[] }) {
  if (!nodes?.length) return null;
  return (
    <>
      {nodes.map((node, index) => (
        <RenderNode key={index} node={node} />
      ))}
    </>
  );
}

function mediaFrame(node: Node): Frame | null {
  const media = node.value as
    | { url?: string; alt?: string; width?: number; height?: number }
    | undefined;
  if (!media?.url) return null;
  return {
    url: media.url,
    alt: media.alt ?? '',
    width: media.width,
    height: media.height,
  };
}

function RenderNode({ node }: { node: Node }): ReactNode {
  switch (node.type) {
    case 'text':
      return <Text node={node} />;

    case 'linebreak':
      return <br />;

    case 'paragraph': {
      // Lexical emits an empty paragraph for a blank line. Rendering it draws a
      // gap the editor did not ask for on top of the margin that already exists.
      if (!node.children?.length) return null;
      return (
        <p className={cx('mt-5 first:mt-0', alignment(node))}>
          <Children nodes={node.children} />
        </p>
      );
    }

    case 'heading':
      return <Heading node={node} />;

    case 'quote':
      return (
        <blockquote className="mt-8 border-l-2 border-accent-400 pl-5 text-title font-normal text-ink-800">
          <Children nodes={node.children} />
        </blockquote>
      );

    case 'list': {
      const ordered = node.listType === 'number' || node.tag === 'ol';
      const Tag = ordered ? 'ol' : 'ul';
      return (
        <Tag
          className={cx(
            'mt-5 space-y-2 pl-6',
            ordered ? 'list-decimal' : 'list-disc',
            'marker:text-ink-400'
          )}
        >
          <Children nodes={node.children} />
        </Tag>
      );
    }

    case 'listitem':
      return (
        <li className="pl-1">
          <Children nodes={node.children} />
        </li>
      );

    case 'link':
    case 'autolink':
      return <Link node={node} />;

    case 'upload':
      return <Figure node={node} />;

    case 'horizontalrule':
      return <hr className="mt-10 border-0 border-t border-ink-150" />;

    default:
      // See the note at the top: draw what is inside rather than nothing.
      return <Children nodes={node.children} />;
  }
}

/**
 * A run of text, with Lexical's format bits applied.
 *
 * The bits nest rather than exclude each other — bold *and* italic is a real
 * combination — so this wraps rather than switches.
 */
function Text({ node }: { node: Node }) {
  const text = node.text ?? '';
  if (!text) return null;

  const format = typeof node.format === 'number' ? node.format : 0;
  let element: ReactNode = text;

  if (format & IS_CODE) {
    element = (
      <code className="rounded bg-ink-50 px-1.5 py-0.5 font-mono text-body-sm text-ink-800 ring-1 ring-ink-150">
        {element}
      </code>
    );
  }
  if (format & IS_BOLD) element = <strong className="font-semibold text-ink-950">{element}</strong>;
  if (format & IS_ITALIC) element = <em>{element}</em>;
  if (format & IS_STRIKETHROUGH) element = <s>{element}</s>;
  if (format & IS_UNDERLINE) element = <u>{element}</u>;
  if (format & IS_SUBSCRIPT) element = <sub>{element}</sub>;
  if (format & IS_SUPERSCRIPT) element = <sup>{element}</sup>;

  return <>{element}</>;
}

/**
 * A heading, one level down from where the editor put it.
 *
 * The article's own `<h1>` is the post title, drawn by the route. An editor
 * writing an `h1` in the body would give the page two, so everything shifts:
 * their h1 becomes an h2, and so on. The *size* follows the level, so the
 * document still looks the way it was written.
 */
function Heading({ node }: { node: Node }) {
  const level = Number(String(node.tag ?? 'h2').replace('h', '')) || 2;
  const shifted = Math.min(level + 1, 6);
  const Tag = `h${shifted}` as 'h2';

  const size =
    shifted <= 2
      ? 'text-display-sm sm:text-display mt-12'
      : shifted === 3
        ? 'text-display-sm mt-10'
        : 'text-title mt-8';

  return (
    <Tag className={cx('text-balance text-ink-950', size)}>
      <Children nodes={node.children} />
    </Tag>
  );
}

function Link({ node }: { node: Node }) {
  const href = node.fields?.url ?? node.url;
  if (!href) return <Children nodes={node.children} />;

  const external = /^https?:\/\//i.test(href) && !href.includes('reviewsjet.com');
  const newTab = node.fields?.newTab ?? node.newTab ?? false;

  return (
    <a
      href={href}
      /*
        `noreferrer` rides along with any `target="_blank"`. Without it the
        opened page gets a handle on this one through `window.opener` — the same
        rule `ButtonAnchor` follows.
      */
      target={newTab ? '_blank' : undefined}
      rel={newTab || external ? 'noreferrer' : undefined}
      className="font-medium text-accent-600 underline decoration-accent-200 decoration-1 underline-offset-[3px] transition-colors duration-[var(--duration-fast)] hover:text-accent-700 hover:decoration-accent-600"
    >
      <Children nodes={node.children} />
    </a>
  );
}

/**
 * One image at the column's width, and a button to see it at its own.
 *
 * A body screenshot is a whole dashboard window drawn in a reading column, so
 * it shows *where* a control is and not *what it says* — and the second half is
 * what a reader following a procedure needs. The image is therefore the
 * control: click it and the capture opens full screen.
 */
function Figure({ node }: { node: Node }) {
  const frame = mediaFrame(node);
  const openLightbox = useLightbox();
  if (!frame) return null;

  return (
    <figure className="mt-8">
      <button
        type="button"
        aria-label={`Enlarge: ${frame.alt}`}
        onClick={() => openLightbox?.(frame)}
        className="group relative block w-full cursor-zoom-in"
      >
        <img
          src={frame.url}
          alt={frame.alt}
          width={frame.width}
          height={frame.height}
          loading="lazy"
          decoding="async"
          className="h-auto w-full rounded-xl ring-1 ring-ink-150"
        />
        <span
          className={cx(
            'pointer-events-none absolute right-3 bottom-3 inline-flex items-center gap-1.5',
            'rounded-md bg-ink-950/70 px-2 py-1 text-micro font-medium text-white',
            'opacity-0 transition-opacity duration-[var(--duration-fast)]',
            'group-hover:opacity-100 group-focus-visible:opacity-100'
          )}
        >
          <ZoomIcon />
          Click to enlarge
        </span>
      </button>
      {frame.alt ? (
        <figcaption className="mt-3 text-caption text-ink-500">{frame.alt}</figcaption>
      ) : null}
    </figure>
  );
}

function alignment(node: Node): string | undefined {
  switch (node.format) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    case 'justify':
      return 'text-justify';
    default:
      return undefined;
  }
}
